// RolePickerModal — modal shown when the user holds more than one role and
// has not yet selected one. Blocks admin-page interaction until a role is
// chosen (task 42.2, DESIGN.md §7.5 + §9).
//
// Visual treatment is deliberately minimal for the POC — task 43 owns
// finalized tokens, typography scale, and the `<RoleChip>` component. What
// this module guarantees today:
//   - `role="dialog"` + `aria-modal="true"` + `aria-labelledby`
//   - Focus trapped to the dialog container via `aria-modal`; first button
//     receives initial focus on mount
//   - No close affordance: the dialog is non-dismissible until a role is
//     chosen (that's the whole point of "block admin nav")
//   - Keyboard: arrow keys move focus between role options; Enter activates.

import { useEffect, useRef } from "react";
import { useRole } from "@/role";

export function RolePickerModal(): JSX.Element | null {
  const { status, distinctRoles, setRole } = useRole();
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus the first option on mount so keyboard users can activate without
  // tabbing past the page content behind the scrim.
  useEffect(() => {
    if (status !== "pending") return;
    const firstButton = containerRef.current?.querySelector<HTMLButtonElement>(
      "button[data-role-option]",
    );
    firstButton?.focus();
  }, [status]);

  if (status !== "pending") return null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const buttons = Array.from(
      containerRef.current?.querySelectorAll<HTMLButtonElement>(
        "button[data-role-option]",
      ) ?? [],
    );
    const activeIndex = buttons.findIndex((b) => b === document.activeElement);
    if (activeIndex < 0) return;
    e.preventDefault();
    const delta = e.key === "ArrowDown" ? 1 : -1;
    const next = buttons[(activeIndex + delta + buttons.length) % buttons.length];
    next?.focus();
  };

  return (
    <div
      className="role-picker-scrim"
      role="dialog"
      aria-modal="true"
      aria-labelledby="role-picker-title"
      aria-describedby="role-picker-desc"
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      <div className="role-picker-card">
        <h2 id="role-picker-title" className="role-picker-card__title">
          Select your role
        </h2>
        <p id="role-picker-desc" className="role-picker-card__desc">
          Pick the role you'll act under for this session. You can switch later
          from the top bar.
        </p>
        <ul className="role-picker-card__list">
          {distinctRoles.map((role) => (
            <li key={role}>
              <button
                type="button"
                className="role-picker-card__option"
                data-role-option
                onClick={() => setRole(role)}
              >
                <span className="role-picker-card__option-name">{role}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
